import { ref } from 'vue'
import { parseEther, parseUnits } from 'viem'
import { 
    walletConfig, 
    walletModal,
    PLATFORM_WALLET_ADDRESS
} from './config'
import {
    getAccount,
    sendTransaction,
    waitForTransactionReceipt,
    watchConnections,
    disconnect,
    writeContract,
} from '@wagmi/core';
import { refundUSDC } from '@/services/api';

const userAddress = ref(verifyWalletConnection());
export const platformReceipt = ref(null);
export const sellerReceipt = ref(null);

export const walletStatus = ref(null);

export function verifyWalletConnection() {
    const account = getAccount(walletConfig)
    if (account.status === 'connected') {
        userAddress.value = account.account;
        return true;
    } else {
        return false;
    }
}

export function openWalletModal() {
    walletModal.open().catch(error => {
        console.error('Error opening wallet modal:', error);
    });
}

export function calculatePaymentSplit(totalAmount, fee) {
    const platformFee = fee > 0 ? (Number(totalAmount) * Number(fee)) / 100 : 0;
    const sellerAmount = Number(totalAmount) - Number(platformFee);

    return {
        platformFee: parseUnits(platformFee.toString(), 6),
        sellerAmount: parseUnits(sellerAmount.toString(), 6)
    };
}

// USDC token contract addresses
const USDC_CONTRACT_ADDRESSES = {
    base: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', // Base Mainnet USDC
    baseSepolia: '0x036CbD53842c5426634e7929541eC2318f3dCF7e' // Base Sepolia USDC
}

// ERC20 ABI for token transfers
const ERC20_ABI = [
    {
        "inputs": [
            {"name": "to", "type": "address"},
            {"name": "amount", "type": "uint256"}
        ],
        "name": "transfer",
        "outputs": [{"name": "", "type": "bool"}],
        "stateMutability": "",
        "type": "function"
    }
]

// Function to send native ETH with platform fee
export async function execTransation(value, to, fee) {
    try {
        const paymentSplit = calculatePaymentSplit(Number.parseFloat(value), fee);

        const sellerTxResponse = await sendTransaction(walletConfig, {
            to: to,
            value: parseEther(paymentSplit.sellerAmount.toString())
        });

        const platformTxResponse = await sendTransaction(walletConfig, {
            to: PLATFORM_WALLET_ADDRESS,
            value: parseEther(paymentSplit.platformFee.toString())
        });

        const sellerReceipt = await waitForTransactionReceipt(walletConfig, {
            hash: sellerTxResponse,
        });

        const platformReceipt = await waitForTransactionReceipt(walletConfig, {
            hash: platformTxResponse,
        });

        return {
            sellerReceipt,
            platformReceipt,
            paymentSplit
        };
    } catch (error) {
        if (error.code === 4001) { 
            console.log('Transaction declined by user.');
        } else {
            console.error('Error executing ETH transaction:', error);
        }
        return null;
    }
}

export async function sendUSDC(amount, sellerAddress, fee) {
    try {
        const paymentSplit = calculatePaymentSplit(Number.parseFloat(amount), Number(fee));
        // Get the current network from the account
        const account = getAccount(walletConfig);
        const chainId = account.chainId;
        // Determine which USDC contract to use based on the chain
        const contractAddress = chainId === 84532 
            ? USDC_CONTRACT_ADDRESSES.baseSepolia 
            : USDC_CONTRACT_ADDRESSES.base;
        // USDC has 6 decimals
        if(paymentSplit.platformFee > 0) {
            const platformHash = await writeContract(walletConfig, {
                address: contractAddress,
                abi: ERC20_ABI,
                functionName: 'transfer',
                args: [PLATFORM_WALLET_ADDRESS, paymentSplit.platformFee]
            });
            platformReceipt.value = await waitForTransactionReceipt(walletConfig, {
                hash: platformHash,
            });
        }
        const sellerHash = await writeContract(walletConfig, {
            address: contractAddress,
            abi: ERC20_ABI,
            functionName: 'transfer',
            args: [sellerAddress, paymentSplit.sellerAmount]
        });
        sellerReceipt.value = await waitForTransactionReceipt(walletConfig, {
            hash: sellerHash,
        });
        
        return true;
    } catch (error) {
        if (error.code === 4001) { 
            console.log('Transaction declined by user.');
        } else {
            console.error('Error executing USDC transaction:', error);
            if(platformReceipt.value && !sellerReceipt.value) {
                refundUSDC(platformReceipt.value.transactionHash);
            }
            platformReceipt.value = null;
            sellerReceipt.value = null;
        }
        return false;
    }
}

export async function disconnectWallet() {
    const { connector } = getAccount(walletConfig)
    await disconnect(walletConfig, { connector });
}
watchConnections(walletConfig, {
    onChange(data) {
        walletStatus.value = data.length > 0;
    },
});
